import React from 'react';
import {
  Container,
  Header,
  KeyValuePairs,
  Badge,
  StatusIndicator,
} from '@cloudscape-design/components';
import { Question } from 'src/types/helios/Question';
import { formatDateTime } from 'src/utils/generalUtils';
import 'src/global.css';

interface QuestionReadonlyDetailsProps {
  question: Question;
  title?: string;
  getBusinessUnitName?: (businessUnitId: string) => string;
  selectedLanguage?: string;
}

export const QuestionReadonlyDetails: React.FC<QuestionReadonlyDetailsProps> = ({
  question,
  title = 'Basic Info',
  getBusinessUnitName,
  selectedLanguage,
}) => {
  const renderTags = (tagMap: { [key: string]: string[] } | undefined) => {
    if (!tagMap || Object.keys(tagMap).length === 0) {
      return '-';
    }

    return (
      <div className="tag-container">
        {Object.entries(tagMap).map(([tagGroupName, tagValues]) => {
          return tagValues.map((data, index) => {
            const sanitizedKey = `${tagGroupName}-${data}-${index}`.replace(
              /[^a-zA-Z0-9-]/g,
              '-',
            );
            return (
              <span key={sanitizedKey} className="tag">
                {tagGroupName} - {data}
              </span>
            );
          });
        })}
      </div>
    );
  };

  const renderLocations = (locations: string[] | undefined) => {
    if (!locations || locations.length === 0) {
      return '-';
    }

    return (
      <div className="location-container">
        {locations.map((item) => (
          <span key={item} className="location">
            {item}
          </span>
        ))}
      </div>
    );
  };

  // Check if viewing a translation (not default language)
  const isViewingTranslation =
    selectedLanguage && selectedLanguage !== question.defaultLanguage;
  const translationMetadata = isViewingTranslation
    ? question.localeTranslationContentMap?.[selectedLanguage]
    : null;

  // Use translation-specific metadata if viewing a translation, otherwise use question metadata
  const lastUpdatedBy = translationMetadata?.lastUpdatedBy || question.lastUpdatedBy;
  const lastUpdatedAt = translationMetadata?.lastUpdatedAt || question.lastUpdatedAt;

  const detailsFields = [
    {
      label: 'Question ID',
      value: <Badge color="blue">{question.questionId}</Badge>,
    },
    { label: 'Version', value: question.version },
    {
      label: 'Business Unit',
      value: getBusinessUnitName
        ? getBusinessUnitName(question.businessUnit)
        : question.businessUnit,
    },
    { label: 'Division Name', value: question.divisionName },
    { label: 'Category Name', value: question.categories?.join(', ') || '-' },
    {
      label: 'AuditScore',
      value: <Badge color="severity-low">{question.auditScore}</Badge>,
    },
    {
      label: 'Status',
      value: (
        <StatusIndicator type={question.isActive ? 'success' : 'error'}>
          {question.isActive ? 'Active' : 'Inactive'}
        </StatusIndicator>
      ),
    },
    {
      label: 'Last Updated At',
      value: formatDateTime(lastUpdatedAt || ''),
    },
    { label: 'Last Updated By', value: lastUpdatedBy || '-' },
    { label: 'Translation Status', value: question.translationStatus || '-' },
    ...(question.locationsExcluded && question.locationsExcluded.length > 0
      ? [
          {
            label: 'Locations Excluded',
            value: renderLocations(question.locationsExcluded),
          },
        ]
      : []),
    ...(question.tagsIncluded && Object.keys(question.tagsIncluded).length > 0
      ? [
          {
            label: 'Tags Included',
            value: renderTags(question.tagsIncluded),
          },
        ]
      : []),
    ...(question.tagsExcluded && Object.keys(question.tagsExcluded).length > 0
      ? [
          {
            label: 'Tags Excluded',
            value: renderTags(question.tagsExcluded),
          },
        ]
      : []),
  ];

  return (
    <Container header={<Header variant="h3">{title}</Header>}>
      <KeyValuePairs columns={3} items={detailsFields} />
    </Container>
  );
};
